'use client';

import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { Button } from '@/core/presentation/components/ui/Button';
import { Radio, RadioGroup } from '@/core/presentation/components/ui/Radio';
import AppIcon from '@/core/presentation/components/ui/AppIcon';
import { Spinner } from '@/core/presentation/components/ui/Spinner';
import { CustomSelect } from '@/core/presentation/components/ui/CustomSelect';
import { CustomDateInput } from '@/core/presentation/components/ui/CustomDateInput';
import { SectionScreenLayout } from '@/features/app/presentation/components/SectionScreenLayout';
import { formatPhone } from '@/core/utils/formatPhone';
import { useServices } from '../context/ServiceContext';
import { useClients } from '@/features/app/clients/presentation/context/ClientContext';
import { useAwaitInitialFetch } from '@/features/app/presentation/hooks/use-await-initial-fetch';
import { APP_SECTION_LOADING_MESSAGE } from '@/features/app/presentation/constants/app-shell-copy';
import { useToast } from '@/core/presentation/components/Toast';
import { SERVICE_TYPE_LABELS, type Service, type ServiceType } from '@/core/domain';
import {
  FORM_FIELD_CONTROL_APP,
  FORM_FIELD_LABEL_APP,
  FORM_FIELD_READOUT_APP,
  FORM_FIELD_ROW_HEIGHT_CLASS,
} from '@/core/presentation/constants/form-field-ui.constants';

/** Tipos de serviço com sugestão de meses para próxima manutenção (ramo de ar condicionado). */
const TIPOS_SERVICO: { value: ServiceType; label: string; mesesProxima: number }[] = [
  { value: 'instalacao', label: SERVICE_TYPE_LABELS.instalacao, mesesProxima: 6 },
  { value: 'limpeza', label: SERVICE_TYPE_LABELS.limpeza, mesesProxima: 3 },
  { value: 'manutencao_preventiva', label: SERVICE_TYPE_LABELS.manutencao_preventiva, mesesProxima: 6 },
  { value: 'manutencao_corretiva', label: SERVICE_TYPE_LABELS.manutencao_corretiva, mesesProxima: 6 },
  { value: 'recarga_gas', label: SERVICE_TYPE_LABELS.recarga_gas, mesesProxima: 6 },
  { value: 'troca_filtro', label: SERVICE_TYPE_LABELS.troca_filtro, mesesProxima: 3 },
  { value: 'higienizacao', label: SERVICE_TYPE_LABELS.higienizacao, mesesProxima: 3 },
  { value: 'manutencao', label: SERVICE_TYPE_LABELS.manutencao, mesesProxima: 6 },
  { value: 'outro', label: SERVICE_TYPE_LABELS.outro, mesesProxima: 6 },
];

/** Atalhos rápidos de período (meses) */
const PERIODOS_RAPIDOS = [3, 6, 9, 12] as const;
const PERIODO_MIN = 1;
const PERIODO_MAX = 60;

const inputClasses = FORM_FIELD_CONTROL_APP;
const labelClasses = FORM_FIELD_LABEL_APP;

/** Retorna YYYY-MM-DD da data de hoje */
function todayISO(): string {
  const d = new Date();
  return d.toISOString().slice(0, 10);
}

/** Soma meses a uma data YYYY-MM-DD e retorna YYYY-MM-DD */
function addMonths(dateStr: string, months: number): string {
  if (!dateStr) return todayISO();
  const d = new Date(dateStr + 'T12:00:00');
  d.setMonth(d.getMonth() + months);
  return d.toISOString().slice(0, 10);
}

/** Formata telefone para exibição (aceita número com ou sem código do país). */
function displayPhone(phone: string): string {
  const digits = phone.replace(/\D/g, '');
  const national = digits.length > 10 ? digits.replace(/^55/, '') : digits;
  return formatPhone(national);
}

type ClientMode = 'new' | 'existing';

export interface RegisterServiceSectionProps {
  /** Cliente pré-selecionado ao vir do histórico do cliente (ex.: "Registrar novo serviço") */
  preselectedClient?: { id: string; nome: string; whatsapp: string; endereco?: string } | null;
  /** Chamado após aplicar o cliente pré-selecionado no formulário */
  onPreselectedApplied?: () => void;
  /** Serviço pré-selecionado para edição ao vir da lista de serviços */
  preselectedServiceForEdit?: Service | null;
  /** Chamado após aplicar o serviço pré-selecionado no formulário */
  onPreselectedServiceApplied?: () => void;
}

export const RegisterServiceSection: React.FC<RegisterServiceSectionProps> = ({
  preselectedClient,
  onPreselectedApplied,
  preselectedServiceForEdit,
  onPreselectedServiceApplied,
}) => {
  const { events: serviceEvents } = useServices();
  const { state: clientState, events: clientEvents } = useClients();

  const loadFormLists = useCallback(async () => {
    await Promise.all([clientEvents.getByUserId(), serviceEvents.getByUserId()]);
  }, [clientEvents.getByUserId, serviceEvents.getByUserId]);
  const formListsReady = useAwaitInitialFetch(loadFormLists, [loadFormLists]);
  const toast = useToast();
  const [clientMode, setClientMode] = useState<ClientMode>('new');
  const [selectedClientId, setSelectedClientId] = useState<string | null>(null);
  const [nome, setNome] = useState('');
  const [whatsapp, setWhatsapp] = useState('');
  const [endereco, setEndereco] = useState('');
  const [tipoServico, setTipoServico] = useState<string>(TIPOS_SERVICO[0].value);
  const [tipoOutroDescricao, setTipoOutroDescricao] = useState('');
  const [referenciaEquipamento, setReferenciaEquipamento] = useState('');
  const [dataServico, setDataServico] = useState(todayISO());
  const [periodoMeses, setPeriodoMeses] = useState<number>(6);
  const [periodoModo, setPeriodoModo] = useState<'rapido' | 'outro'>('rapido');
  const [editingServiceId, setEditingServiceId] = useState<string | null>(null);
  const [submitBusy, setSubmitBusy] = useState(false);

  const dataProximaManutencao = useMemo(
    () => addMonths(dataServico, periodoMeses),
    [dataServico, periodoMeses]
  );

  useEffect(() => {
    if (preselectedClient) {
      setEditingServiceId(null);
      setClientMode('existing');
      setSelectedClientId(preselectedClient.id);
      setNome(preselectedClient.nome);
      setWhatsapp(preselectedClient.whatsapp);
      setEndereco(preselectedClient.endereco ?? '');
      onPreselectedApplied?.();
    }
  }, [preselectedClient, onPreselectedApplied]);

  useEffect(() => {
    if (!preselectedServiceForEdit) return;
    setEditingServiceId(preselectedServiceForEdit.id);
    setClientMode('existing');
    setSelectedClientId(preselectedServiceForEdit.client_id);
    setTipoServico(preselectedServiceForEdit.type);
    setTipoOutroDescricao(preselectedServiceForEdit.type_other_description ?? '');
    setReferenciaEquipamento(preselectedServiceForEdit.equipment_reference ?? '');
    setDataServico(preselectedServiceForEdit.service_date);
    setPeriodoMeses(preselectedServiceForEdit.next_maintenance_months);
    setPeriodoModo(
      PERIODOS_RAPIDOS.includes(preselectedServiceForEdit.next_maintenance_months as 3 | 6 | 9 | 12)
        ? 'rapido'
        : 'outro'
    );

    const selectedClient = clientState.clients.find(
      (c) => c.id === preselectedServiceForEdit.client_id
    );
    if (selectedClient) {
      setNome(selectedClient.name);
      setWhatsapp(selectedClient.phone);
      setEndereco(selectedClient.address ?? '');
    }
    onPreselectedServiceApplied?.();
  }, [
    preselectedServiceForEdit,
    onPreselectedServiceApplied,
    clientState.clients,
  ]);

  const handleWhatsAppChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setWhatsapp(formatPhone(e.target.value));
  };

  const handleTipoChange = (newValue: string) => {
    setTipoServico(newValue);
    const t = TIPOS_SERVICO.find((x) => x.value === newValue);
    if (t) {
      setPeriodoMeses(t.mesesProxima);
      setPeriodoModo(PERIODOS_RAPIDOS.includes(t.mesesProxima as 3 | 6 | 9 | 12) ? 'rapido' : 'outro');
    }
  };

  const setPeriodoFromInput = (v: number) => {
    const clamped = Math.max(PERIODO_MIN, Math.min(PERIODO_MAX, v));
    setPeriodoMeses(clamped);
  };

  const selecionarPeriodoRapido = (m: number) => {
    setPeriodoMeses(m);
    setPeriodoModo('rapido');
  };

  const selecionarOutro = () => {
    setPeriodoModo('outro');
    if (PERIODOS_RAPIDOS.includes(periodoMeses as 3 | 6 | 9 | 12)) {
      setPeriodoMeses(6);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitBusy) return;
    setSubmitBusy(true);
    try {
      const result = editingServiceId
        ? await serviceEvents.update(editingServiceId, {
            client_id: selectedClientId ?? undefined,
            type: tipoServico as ServiceType,
            type_other_description: tipoServico === 'outro' ? (tipoOutroDescricao.trim() || null) : null,
            service_date: dataServico,
            next_maintenance_months: periodoMeses,
            next_maintenance_date: dataProximaManutencao,
            equipment_reference: referenciaEquipamento.trim() || null,
          })
        : await serviceEvents.create({
            ...(clientMode === 'existing' && selectedClientId
              ? { clientId: selectedClientId }
              : {
                  newClient: {
                    name: nome.trim(),
                    phone: whatsapp.trim(),
                    address: endereco.trim() || null,
                  },
                }),
            type: tipoServico as ServiceType,
            type_other_description: tipoServico === 'outro' ? (tipoOutroDescricao.trim() || null) : null,
            service_date: dataServico,
            next_maintenance_months: periodoMeses,
            next_maintenance_date: dataProximaManutencao,
            equipment_reference: referenciaEquipamento.trim() || null,
            value: null,
            notes: null,
          });
      if (result.success) {
        toast.success(editingServiceId ? 'Serviço atualizado.' : 'Serviço registrado.');
        setNome('');
        setWhatsapp('');
        setEndereco('');
        setReferenciaEquipamento('');
        setTipoOutroDescricao('');
        setDataServico(todayISO());
        setPeriodoMeses(6);
        setSelectedClientId(null);
        setClientMode('new');
        setEditingServiceId(null);
      } else {
        toast.error(
          result.error ??
            (editingServiceId
              ? 'Não foi possível atualizar o serviço.'
              : 'Não foi possível registrar o serviço.')
        );
      }
    } finally {
      setSubmitBusy(false);
    }
  };

  const canSubmit = editingServiceId
    ? !!selectedClientId
    : nome.trim().length > 0 && whatsapp.replace(/\D/g, '').length >= 10;

  return (
    <SectionScreenLayout
      title={editingServiceId ? 'Editar serviço' : 'Registrar serviço'}
      subtitle={
        editingServiceId
          ? 'Atualize os dados do serviço. A próxima manutenção é recalculada automaticamente.'
          : 'Preencha os dados do cliente e do serviço. A próxima manutenção é sugerida automaticamente.'
      }
    >
      {!formListsReady ? (
        <div className="flex items-center gap-2 text-text-muted text-sm py-6">
          <Spinner size="sm" className="text-cyan" aria-hidden />
          <span>{APP_SECTION_LOADING_MESSAGE}</span>
        </div>
      ) : (
      <form onSubmit={handleSubmit} className="space-y-8 min-w-0 max-w-full">
        {/* Cliente: rádio — novo cliente (form) ou selecionar existente (lista) */}
        <section className="space-y-4">
          <h2
            id="rs-client-section-title"
            className="text-sm font-semibold text-navy-dark uppercase tracking-wide flex items-center gap-2"
          >
            <AppIcon name="UserIcon" size={18} className="text-cyan shrink-0" />
            Cliente
          </h2>

          <RadioGroup name="register-service-client-mode" aria-labelledby="rs-client-section-title">
            <Radio
              value="new"
              checked={clientMode === 'new'}
              onChange={() => {
                setClientMode('new');
                setSelectedClientId(null);
                setNome('');
                setWhatsapp('');
                setEndereco('');
              }}
              label="Adicionar novo cliente"
            />
            <Radio
              value="existing"
              checked={clientMode === 'existing'}
              onChange={() => setClientMode('existing')}
              label="Selecionar cliente existente"
            />
          </RadioGroup>

          {clientMode === 'new' && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="sm:col-span-2">
                <label htmlFor="rs-nome" className={labelClasses}>
                  Nome <span className="text-red-500">*</span>
                </label>
                <input
                  id="rs-nome"
                  type="text"
                  value={nome}
                  onChange={(e) => setNome(e.target.value)}
                  placeholder="Nome do cliente"
                  className={inputClasses}
                  required
                  aria-readonly={false}
                />
              </div>
              <div>
                <label htmlFor="rs-whatsapp" className={labelClasses}>
                  WhatsApp <span className="text-red-500">*</span>
                </label>
                <input
                  id="rs-whatsapp"
                  type="tel"
                  value={whatsapp}
                  onChange={handleWhatsAppChange}
                  placeholder="(00) 00000-0000"
                  inputMode="tel"
                  autoComplete="tel"
                  className={inputClasses}
                  required
                  aria-readonly={false}
                />
              </div>
              <div>
                <label htmlFor="rs-endereco" className={labelClasses}>
                  Endereço <span className="text-text-muted text-xs font-normal">(opcional)</span>
                </label>
                <input
                  id="rs-endereco"
                  type="text"
                  value={endereco}
                  onChange={(e) => setEndereco(e.target.value)}
                  placeholder="Endereço"
                  className={inputClasses}
                  aria-readonly={false}
                />
              </div>
            </div>
          )}

          {clientMode === 'existing' && (
            <>
              {clientState.error && (
                <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800 mb-3">
                  {clientState.error}
                </div>
              )}
              {clientState.loading && !clientState.clients.length ? (
                <div className="flex items-center justify-center gap-2 text-text-muted text-sm py-4">
                  <Spinner size="sm" className="text-cyan" aria-hidden />
                  <span>Carregando clientes</span>
                </div>
              ) : clientState.clients.length === 0 ? (
                <div className="rounded-xl border border-border-light bg-[#fafbfc] px-4 py-6 text-center">
                  <p className="text-text-muted text-sm">
                    Nenhum cliente cadastrado. Marque &quot;Adicionar novo cliente&quot; acima ou registre um serviço preenchendo nome e WhatsApp para criar o cliente.
                  </p>
                </div>
              ) : (
                <div className="rounded-xl border border-border-light bg-white overflow-hidden max-h-48 overflow-y-auto">
                  {clientState.clients.map((c) => (
                    <button
                      key={c.id}
                      type="button"
                      onClick={() => {
                        setSelectedClientId(c.id);
                        setNome(c.name);
                        setWhatsapp(c.phone);
                        setEndereco(c.address ?? '');
                      }}
                      className={`w-full text-left px-4 py-3 border-b border-border-light last:border-b-0 focus:outline-none focus:bg-cyan/5 ${
                        selectedClientId === c.id ? 'bg-cyan/15 font-medium text-cyan-dark' : 'hover:bg-[#fafbfc]'
                      }`}
                    >
                      <span className="block font-medium text-navy-dark">{c.name}</span>
                      <span className="block text-sm text-text-muted">{displayPhone(c.phone)}</span>
                    </button>
                  ))}
                </div>
              )}
            </>
          )}

          {clientMode === 'existing' && selectedClientId && (
            <p className="text-sm text-text-muted">
              Cliente selecionado: <span className="font-medium text-navy-dark">{nome}</span>. Os dados serão usados no registro.
            </p>
          )}
        </section>

        {/* Serviço realizado */}
        <section className="space-y-4 pt-6 border-t border-border-light">
          <h2 className="text-sm font-semibold text-navy-dark uppercase tracking-wide flex items-center gap-2">
            <AppIcon name="WrenchScrewdriverIcon" size={18} className="text-cyan shrink-0" />
            Serviço realizado
          </h2>
          <CustomSelect
            label="Que tipo de serviço foi realizado?"
            id="rs-tipo"
            value={tipoServico}
            onChange={handleTipoChange}
            options={TIPOS_SERVICO.map((t) => ({ value: t.value, label: t.label }))}
            placeholder="Selecione o tipo"
          />
          {tipoServico === 'outro' && (
            <div>
              <label htmlFor="rs-tipo-outro" className={labelClasses}>
                Descreva o serviço
              </label>
              <input
                id="rs-tipo-outro"
                type="text"
                value={tipoOutroDescricao}
                onChange={(e) => setTipoOutroDescricao(e.target.value)}
                placeholder="Ex.: Troca de ventilador, reparo no motor..."
                className={inputClasses}
              />
            </div>
          )}
          <div>
            <label htmlFor="rs-referencia" className={labelClasses}>
              Descrição ou referência do equipamento/ambiente{' '}
              <span className="text-text-muted text-xs font-normal">(opcional)</span>
            </label>
            <input
              id="rs-referencia"
              type="text"
              value={referenciaEquipamento}
              onChange={(e) => setReferenciaEquipamento(e.target.value)}
              placeholder="Ex.: Sala A, marca LG, ar do escritório — para lembrar qual equipamento é"
              className={inputClasses}
            />
            <p className="mt-1 text-xs text-text-muted">
              Útil quando o mesmo cliente tem mais de um ar: sala A, sala B, marca, etc.
            </p>
          </div>
        </section>

        {/* Datas: no desktop título à esquerda e campo à direita em cada linha; no mobile empilhado */}
        <section className="space-y-4 pt-6 border-t border-border-light min-w-0 max-w-full">
          <h2 className="text-sm font-semibold text-navy-dark uppercase tracking-wide flex items-center gap-2">
            <AppIcon name="CalendarDaysIcon" size={18} className="text-cyan shrink-0" />
            Datas
          </h2>

          {/* Coluna direita: largura fixa (11rem, suficiente para data) e alinhada à direita */}
          <div>
            {/* Linha 1: Data do serviço | input */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:gap-4 min-w-0">
              <label htmlFor="rs-data-servico" className={`${labelClasses} sm:mb-0 sm:w-52 sm:shrink-0`}>
                Data do serviço
              </label>
              <div className="w-full min-w-0 sm:w-44 sm:min-w-44 sm:ml-auto">
                <CustomDateInput
                  id="rs-data-servico"
                  value={dataServico}
                  onChange={setDataServico}
                  variant="light"
                  required
                  placeholder="Selecione a data"
                />
              </div>
            </div>

            {/* Linha 2: Próxima manutenção (meses) | opções — largura maior para caber tudo em uma linha */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:gap-4 min-w-0 mt-4">
              <label className={`${labelClasses} sm:mb-0 sm:w-52 sm:shrink-0`}>
                Próxima manutenção (meses)
              </label>
              <div className="w-full min-w-0 sm:w-72 sm:min-w-72 sm:ml-auto space-y-3">
                <div className="flex flex-wrap sm:flex-nowrap gap-1.5 sm:gap-2 items-center min-w-0 sm:justify-end">
                {PERIODOS_RAPIDOS.map((m) => (
                  <button
                    key={m}
                    type="button"
                    onClick={() => selecionarPeriodoRapido(m)}
                    className={`${FORM_FIELD_ROW_HEIGHT_CLASS} w-10 shrink-0 rounded-lg border text-sm font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-cyan focus:ring-offset-2 flex items-center justify-center ${
                      periodoModo === 'rapido' && periodoMeses === m
                        ? 'border-cyan bg-cyan text-navy-dark'
                        : 'border-border-light bg-[#fafbfc] text-text-body hover:bg-cyan/20'
                    }`}
                  >
                    {m}
                  </button>
                ))}
                <button
                  type="button"
                  onClick={selecionarOutro}
                  className={`${FORM_FIELD_ROW_HEIGHT_CLASS} min-w-[3.25rem] shrink-0 rounded-lg border px-2.5 text-sm font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-cyan focus:ring-offset-2 flex items-center justify-center ${
                    periodoModo === 'outro'
                      ? 'border-cyan bg-cyan text-navy-dark'
                      : 'border-border-light bg-[#fafbfc] text-text-body hover:bg-cyan/20'
                  }`}
                >
                  Outro
                </button>
                <span className="text-xs text-text-muted self-center ml-0.5 sm:ml-1 shrink-0">meses</span>
              </div>
              {periodoModo === 'outro' && (
                <div className="flex items-center gap-2">
                  <label htmlFor="rs-periodo-outro" className="text-xs text-text-muted shrink-0">
                    Quantidade de meses:
                  </label>
                  <input
                    id="rs-periodo-outro"
                    type="number"
                    min={PERIODO_MIN}
                    max={PERIODO_MAX}
                    value={periodoMeses}
                    onChange={(e) => setPeriodoFromInput(Number(e.target.value) || PERIODO_MIN)}
                    className={`${inputClasses} w-24`}
                    aria-label="Número de meses"
                  />
                </div>
              )}
              </div>
            </div>

            {/* Linha 3: Próxima manutenção será em | data */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:gap-4 min-w-0 mt-4">
              <span className="text-xs font-medium text-navy-dark sm:w-52 sm:shrink-0">
                Próxima manutenção será em
              </span>
              <div
                className={`${FORM_FIELD_READOUT_APP} sm:ml-auto`}
                aria-live="polite"
              >
                <span className="block min-w-0 truncate">
                  {new Date(dataProximaManutencao + 'T12:00:00').toLocaleDateString('pt-BR', {
                    day: '2-digit',
                    month: '2-digit',
                    year: 'numeric',
                  })}
                </span>
              </div>
            </div>
          </div>

          {/* Quando os avisos serão enviados */}
          <div className="mt-4 rounded-lg border border-cyan/30 bg-cyan/5 px-4 py-3 flex gap-3">
            <AppIcon name="ChatBubbleLeftRightIcon" size={20} className="text-cyan shrink-0 mt-0.5" />
            <div className="min-w-0">
              <p className="text-sm font-medium text-navy-dark">
                Quando você receberá os avisos
              </p>
              <p className="text-sm text-text-muted mt-0.5">
                Você receberá uma mensagem <strong>7 dias antes</strong>, <strong>1 dia antes</strong> e <strong>no dia</strong> da manutenção prevista, para não perder o retorno com o cliente.
              </p>
            </div>
          </div>
        </section>

        <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-3 pt-4">
          <Button
            type="submit"
            size="lg"
            className="gap-2 w-full sm:w-auto"
            disabled={!canSubmit || submitBusy}
            loading={submitBusy}
            loadingLabel={editingServiceId ? 'Salvando…' : 'Registrando…'}
          >
            <AppIcon name="CheckIcon" size={20} />
            {editingServiceId ? 'Salvar alterações' : 'Registrar serviço'}
          </Button>
        </div>
      </form>
      )}
    </SectionScreenLayout>
  );
};
